// Session configuration: each app passes its own values (read from its
// existing REACT_APP_* env vars) once, through <SessionProvider config> or
// configureSession(). The module-level functions (refreshSession, signOut,
// ...) read it from here.

export type SessionConfig = {
	/** short app name, e.g. "fleet", "security" (logs only) */
	app: string;
	/** REACT_APP_API_URL: the app's normal API base (company lookups) */
	apiUrl: string;
	/** REACT_APP_ADMIRAL_APP: the sign-in app */
	admiralUrl: string;
	/** REACT_APP_DOMAIN_NAME: the cookie parent domain (l5flt.com, l5fleet.com, level5fleet.com) */
	domainName: string;
	userPoolId: string;
	clientId: string;
	/** defaults to the region prefix of userPoolId ("us-east-1_abc" -> "us-east-1") */
	region?: string;
	/** defaults from domainName: l5flt.com -> integ_, l5fleet.com -> preprod_, level5fleet.com -> prod_ */
	cookiePrefix?: string;
	/**
	 * Base of the session endpoints (/auth/session, /auth/refresh, /auth/logout).
	 * Defaults to https://app.<domainName>, the API custom domain on the cookie
	 * parent domain (so the HttpOnly refresh cookie is sent). Override for
	 * tests or localhost.
	 */
	sessionApiUrl?: string;
	/** how long refresh/session/logout requests may take before giving up (ms, default 8000) */
	requestTimeoutMs?: number;
};

export type ResolvedSessionConfig = Required<Omit<SessionConfig, "region" | "cookiePrefix" | "sessionApiUrl">> & {
	region: string;
	cookiePrefix: string;
	sessionApiUrl: string;
};

const PREFIX_BY_DOMAIN: Record<string, string> = {
	"l5flt.com": "integ_",
	"l5fleet.com": "preprod_",
	"level5fleet.com": "prod_",
};

const normalizeDomain = (domainName: string | undefined): string =>
	(domainName || "").trim().replace(/^\./, "").toLowerCase();

// The stage's cookie prefix. Unknown domains (localhost) get preprod_, which
// is what the apps used for every non-prod stage before.
export const cookiePrefixFor = (domainName: string | undefined): string =>
	PREFIX_BY_DOMAIN[normalizeDomain(domainName)] ?? "preprod_";

export const defaultSessionApiUrl = (domainName: string | undefined): string =>
	`https://app.${normalizeDomain(domainName)}`;

const stripTrailingSlash = (url: string) => url.replace(/\/+$/, "");

/** The stages' cookie parent domains. */
export const KNOWN_PARENT_DOMAINS = Object.keys(PREFIX_BY_DOMAIN);

/** The known parent domain `host` belongs to (equal or a subdomain), else null. */
export function knownParentDomainOf(host: string | null | undefined): string | null {
	const h = normalizeDomain(host || "");
	if (!h) return null;
	return KNOWN_PARENT_DOMAINS.find((parent) => h === parent || h.endsWith(`.${parent}`)) ?? null;
}

const currentHost = (): string | null => {
	try {
		return typeof window !== "undefined" ? window.location.hostname || null : null;
	} catch {
		return null;
	}
};

const reported = new Set<string>();
const reportOnce = (level: "error" | "warn", message: string) => {
	if (reported.has(message)) return;
	reported.add(message);
	console[level](message);
};

/**
 * The cookie parent domain actually in effect. The page's host wins when it
 * is under a known parent (l5flt.com, l5fleet.com, level5fleet.com): an app
 * built without (or with a wrong) REACT_APP_DOMAIN_NAME would otherwise read
 * another stage's cookie names and loop through sign-in silently. A
 * configured value that disagrees is reported (console.error, once). Off the
 * known domains (localhost, tests) the configured value is used; with none,
 * the result is "" (cookies host-only, prefix preprod_) and a warning.
 */
export function effectiveParentDomain(configured: string | null | undefined, host: string | null = currentHost()): string {
	const fromHost = knownParentDomainOf(host);
	const fromConfig = normalizeDomain(configured || "");
	if (fromHost) {
		if (fromConfig && fromConfig !== fromHost) {
			reportOnce(
				"error",
				`[web-session] REACT_APP_DOMAIN_NAME is "${configured}" but the page is on ${host}: using ${fromHost} (its cookies and session API)`
			);
		}
		return fromHost;
	}
	if (fromConfig) return fromConfig;
	reportOnce(
		"warn",
		`[web-session] no cookie domain: REACT_APP_DOMAIN_NAME is not set and ${host || "this host"} is not under ${KNOWN_PARENT_DOMAINS.join(
			", "
		)}; using host-only cookies with the preprod_ prefix`
	);
	return "";
}

export const resolveConfig = (config: SessionConfig): ResolvedSessionConfig => {
	const domainName = effectiveParentDomain(config.domainName);
	return {
		app: config.app,
		apiUrl: stripTrailingSlash(config.apiUrl || ""),
		admiralUrl: config.admiralUrl || "",
		domainName,
		userPoolId: config.userPoolId || "",
		clientId: config.clientId || "",
		region: config.region || (config.userPoolId || "").split("_")[0] || "us-east-1",
		cookiePrefix: config.cookiePrefix ?? cookiePrefixFor(domainName),
		sessionApiUrl: stripTrailingSlash(config.sessionApiUrl || defaultSessionApiUrl(domainName)),
		requestTimeoutMs: config.requestTimeoutMs ?? 8000,
	};
};

let current: ResolvedSessionConfig | null = null;

export function configureSession(config: SessionConfig): ResolvedSessionConfig {
	current = resolveConfig(config);
	return current;
}

export function getSessionConfig(): ResolvedSessionConfig {
	if (!current) {
		throw new Error("web-session: configureSession() (or <SessionProvider config>) has not been called");
	}
	return current;
}

export const isSessionConfigured = (): boolean => current !== null;

/** tests only */
export function resetSessionConfig(): void {
	current = null;
	reported.clear();
}
